import { useRefs } from '@consta/uikit/useRefs'
import { assert, hasKeysOfUnknown, isObject } from 'common/helpers'
import type { Nullable } from 'common/types'
import type { FC, PropsWithChildren, RefObject } from 'react'
import { createContext, useContext, useMemo } from 'react'

import { isDefined } from '../guards'

const ModalType = ['dispatcher', 'movingModals', 'other'] as const
type ModalType = (typeof ModalType)[number]

type ModalMountElement = Record<ModalType, RefObject<HTMLDivElement>>

const ModalManagerContext = createContext<Nullable<ModalMountElement>>(null)

export const ModalManagerContextProvider: FC<PropsWithChildren> = ({ children }) => {
  const refs = useRefs(ModalType.length)

  const refMap = useMemo(() => {
    const refMap = Object.fromEntries(ModalType.map((type, index) => [type, refs[index]]))
    assert(isRefMap(refMap))
    return refMap
  }, [refs])

  return <ModalManagerContext.Provider value={refMap}>{children}</ModalManagerContext.Provider>
}

function isRefMap(candidate: unknown): candidate is ModalMountElement {
  return isObject(candidate) && hasKeysOfUnknown(candidate, ...ModalType)
}

export const useModalManager = () => {
  const context = useContext(ModalManagerContext)
  assert(isDefined(context), 'Modal Manager context has no provider')
  return context
}
